import { useState, useEffect, useRef } from 'react';
import { Button } from '@voilajsx/uikit/button';
import { Check, Copy } from 'lucide-react';
import Prism from 'prismjs';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-yaml';

interface CodePreviewProps {
  code: string;
  language?: string;
  preview?: React.ReactNode;
  title?: string;
}

export const CodePreview: React.FC<CodePreviewProps> = ({
  code,
  language = 'tsx',
  preview,
  title
}) => {
  const [copied, setCopied] = useState(false);
  const codeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (codeRef.current) {
      Prism.highlightElement(codeRef.current);
    }
  }, [code, language]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Map common language aliases
  const langMap: Record<string, string> = {
    ts: 'typescript',
    js: 'javascript',
    sh: 'bash',
    shell: 'bash',
    html: 'markup',
    text: 'plaintext',
  };

  const prismLang = langMap[language] || language;

  return (
    <div className="border border-border rounded-lg overflow-hidden my-4">
      {title && (
        <div className="px-4 py-2 bg-muted/50 border-b border-border text-sm font-medium flex items-center justify-between">
          <span>{title}</span>
          <span className="text-xs text-muted-foreground uppercase">{language}</span>
        </div>
      )}

      {preview && (
        <div className="p-6 bg-background border-b border-border">
          {preview}
        </div>
      )}

      <div className="relative group">
        <pre className="p-4 bg-[#1e1e1e] overflow-x-auto text-sm leading-relaxed m-0">
          <code
            ref={codeRef}
            className={`language-${prismLang}`}
          >
            {code.trim()}
          </code>
        </pre>
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700"
          onClick={handleCopy}
        >
          {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};
